import * as sentry from 'utilities/sentry';

import * as validate from 'validate';

// http://stackoverflow.com/a/901144/696130
export function getParam(name: string, altLocation?: string): string {
  const filteredName = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');

  const urlParams = new URLSearchParams(altLocation || location.search);
  const result = urlParams.get(filteredName);

  return String(result ? result : '');
}

export function hasParam(
  name: string,
  source: string | undefined = location.search
): boolean {
  const filteredName = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');

  return new URLSearchParams(source).has(filteredName);
}

/**
 * Starts download in same tab from a given string
 * @param {String} download - file name
 * @param {String} href - full link
 */
export function downloadLinkFromString(download: string, href: string): void {
  const link = document.createElement('a');

  link.download = download;
  link.href = href;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

type PublicSharePageResult = {
  fromPublicSharePage: boolean;
  parentLocation: null | string;
};

export function isFromPublicSharePage(): PublicSharePageResult {
  const result: PublicSharePageResult = {
    fromPublicSharePage: false,
    parentLocation: null,
  };

  try {
    if (
      window.parent != null &&
      window.parent.location != null &&
      window.parent.location.pathname != null
    ) {
      result.fromPublicSharePage = new RegExp(
        '^/share/' + validate.VIDEO_ID_REGEX + '$',
        'i'
      ).test(window.parent.location.pathname);
      result.parentLocation = window.parent.location.href;
    }
  } catch (err) {
    const crossOriginError =
      err instanceof window.DOMException ||
      (err as Error).message.toLowerCase().indexOf('permission') > -1;

    if (!crossOriginError) {
      sentry.logException(err, 'error in fromPublicSharePage util', {
        iframe_referrer: document.referrer,
      });
    }
  } finally {
    if (result.parentLocation == null) {
      result.parentLocation = document.referrer;
    }
  }

  return result;
}

export function getFilteredPathnames(path: string): string[] {
  const pathnamesUnfiltered = path.split('/');
  const pathnames = pathnamesUnfiltered.filter(pathname => pathname !== '');

  return pathnames;
}

export function goToNewTab(url: string): void {
  const win = window.open(url, '_blank');

  win?.focus();
}

export function inEmbedPlayer(): boolean {
  return validate.EMBED_VIDEO_ROUTE_PATHNAME_REGEX.test(
    window.location.pathname
  );
}

const doesNotStartWith =
  (key: string) =>
  (p: string): boolean =>
    !p.startsWith(key);

export const removeParam =
  (window: Window) =>
  (key: string): void => {
    const { pathname, search } = window.location;
    const url = pathname + removeParamFromUrl(key, search);

    window.history.replaceState('', '', url);
  };

const removeParamFromUrl = (key: string, search: string): string => {
  if (search.length === 0 || search.indexOf(key) === -1) {
    return search;
  }

  const filteredParams = search
    .replace('?', '')
    .split('&')
    .filter(doesNotStartWith(key))
    .join('&');

  if (!filteredParams) {
    return '';
  }

  return `?${filteredParams}`;
};

const URL_TRAILING_PUNCTUATION = /[.,;?!:)\]}]+$/;

/**
 * Finds common punctuation characters at the end of a URL and strips them out.
 * @param {String} url - The URL to be inspected.
 * @returns {[string, string]} [trimmedUrl, trail] - A pair with the URL after
 * removing any trailing characters and the trailing characters themselves.
 */
export const splitTrailingPunctuation = (url: string): [string, string] => {
  const matches = url.match(URL_TRAILING_PUNCTUATION);
  const trailing = matches !== null ? matches[0] : '';
  const trimmedUrl = trailing.length
    ? url.substring(0, url.length - trailing.length)
    : url;

  return [trimmedUrl, trailing];
};
